import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    XCircle, Building2, Calendar, Hash, Clock, FileText, AlertTriangle, CheckCircle2,
} from 'lucide-react';

// ─── Shared Inbox Components ──────────────────────────────────────────────────
import InboxHeader       from '../../components/Inbox/InboxHeader';
import ActionButtons     from '../../components/Inbox/ActionButtons';
import RemarksHistory    from '../../components/Inbox/RemarksHistory';
import InboxSplitLayout  from '../../components/Inbox/InboxSplitLayout';
import VerificationInput from '../../components/Inbox/VerificationInput';
import CustomDatePicker  from '../../components/CustomDatePicker';
import { S3_FOLDERS, buildS3Url } from '../../config/s3Config';

// ─── CC Closing slice ─────────────────────────────────────────────────────────
import {
    fetchVerifyCCClosingGrid,
    fetchVerifyCCClosingView,
    submitApproveCCClose,
    clearApproveResult,
    clearVerifyDetail,
    resetCCClosing,
    selectVerifyList,
    selectVerifyDetail,
    selectVerifyListLoading,
    selectVerifyDetailLoading,
    selectApproveLoading,
} from '../../slices/accountsSlice/ccClosingSlice';

// ─── Shared: remarks history ──────────────────────────────────────────────────
import {
    fetchRemarks,
    selectRemarks,
    selectRemarksLoading,
    setSelectedMOID,
} from '../../slices/supplierPOSlice/purcahseHelperSlice';

// ─── Shared: status / action buttons ─────────────────────────────────────────
import {
    fetchStatusList,
    selectEnabledActions,
    selectHasActions,
    selectStatusListLoading,
    selectStatusListError,
    resetApprovalData,
    setShowReturnButton,
} from '../../slices/CommonSlice/getStatusSlice';

import ResourceConsumedGrid, { resourceCsv } from '../../components/Accounts/ResourceConsumedGrid';
import CCClosingSummaryView from '../../components/Accounts/CCClosingSummaryView';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// SP dates come as 'dd-MMM-yyyy' (e.g. 22-Oct-2026)
const parseDMY = (val) => {
    if (!val) return null;
    const m = String(val).trim().match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/);
    if (!m) return null;
    const mon = MONTHS.findIndex(x => x.toLowerCase() === m[2].toLowerCase());
    // noon, like CustomDatePicker's onChange values, so same-day comparisons hold
    return mon < 0 ? null : new Date(Number(m[3]), mon, Number(m[1]), 12);
};
// Legacy datepicker posts 'dd-M-yy' → '22-Oct-2026'
const toDMY = (date) => {
    if (!(date instanceof Date) || isNaN(date)) return '';
    return `${String(date.getDate()).padStart(2, '0')}-${MONTHS[date.getMonth()]}-${date.getFullYear()}`;
};
const today = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12); };
const addDays = (date, n) => { const d = new Date(date); d.setDate(d.getDate() + n); return d; };

// StoreCCCloseMaster ids; the grid's ClosingType text is preferred when present
const CLOSING_TYPE_LABELS = {
    5: 'Store Closing',
    6: 'Store Suspend',
    7: 'Store Closing Reschedule',
    8: 'Store Suspend Reschedule',
};

const CLOSING_TYPE_COLORS = {
    5: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
    6: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    7: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
    8: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
};

const SUCCESS_MSG = {
    verify:  'Verified Successfully',
    approve: 'Approved Successfully',
    return:  'Returned for Update',
    reject:  'Rejected Successfully',
};

// ─── Component ────────────────────────────────────────────────────────────────

const VerifyCCClosing = ({ notificationData, onNavigate }) => {
    const dispatch = useDispatch();

    // ── Selectors ────────────────────────────────────────────────────────────
    const verifyList     = useSelector(selectVerifyList);
    const verifyDetail   = useSelector(selectVerifyDetail);
    const listLoading    = useSelector(selectVerifyListLoading);
    const detailLoading  = useSelector(selectVerifyDetailLoading);
    const approveLoading = useSelector(selectApproveLoading);

    const remarks        = useSelector(selectRemarks);
    const remarksLoading = useSelector(selectRemarksLoading);

    const statusLoading  = useSelector(selectStatusListLoading);
    const statusError    = useSelector(selectStatusListError);
    const enabledActions = useSelector(selectEnabledActions);
    const hasActions     = useSelector(selectHasActions);

    const { userData, userDetails } = useSelector(s => s.auth);
    const roleId   = userData?.roleId   || userData?.RID  || 0;
    const userId   = userData?.userId   || userData?.UID  || userData?.employeeId || '';
    const userName = userData?.userName || userData?.UserName || 'system';
    const roleName = userDetails?.roleName || userData?.roleName || '';

    const { InboxTitle, ModuleDisplayName, RoleId } = notificationData || {};
    const effectiveRoleId = RoleId || roleId;

    // ── Local state ──────────────────────────────────────────────────────────
    const [selectedItem,         setSelectedItem]         = useState(null);
    const [isVerified,           setIsVerified]           = useState(false);
    const [verificationComment,  setVerificationComment]  = useState('');
    const [showRemarksHistory,   setShowRemarksHistory]   = useState(false);
    const [searchQuery,          setSearchQuery]          = useState('');
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isLeftPanelHovered,   setIsLeftPanelHovered]   = useState(false);
    // Legacy VerifyCCClosingView editable fields
    const [closingDate, setClosingDate] = useState(null);   // types 5 (read-only) / 7
    const [startDate,   setStartDate]   = useState(null);   // types 6 / 8
    const [endDate,     setEndDate]     = useState(null);
    const [alertNote,   setAlertNote]   = useState('');
    // Closing type 5: the CC's resource heads with editable details → Heads / HeadDetails CSVs
    const [resourceRows, setResourceRows] = useState([]);
    // Closing type 5: legacy shows Submit only after the CC Closing Summary has loaded
    const [summaryLoaded, setSummaryLoaded] = useState(false);
    // Closing type 5 Approve with PCC budget transfer → confirm first
    const [pendingAction, setPendingAction] = useState(null);

    const getCurrentUser = () => userData?.userName || userDetails?.userName || userName || 'system';

    const typeIdOf = (item) => String(item?.ClosingTypeid ?? item?.SClosingTypeid ?? '');

    const loadGrid = () => dispatch(fetchVerifyCCClosingGrid({
        roleId:  effectiveRoleId,
        created: getCurrentUser(),
        userId,
    }));

    // spVerifyCCClosingView matches @Rid against the record Id (grid IId), not the role
    const loadDetail = (item) => dispatch(fetchVerifyCCClosingView({
        tranno: item.Tranno,
        rid:    item.IId,
        typeid: typeIdOf(item),
    }));

    const resetSelection = () => {
        setSelectedItem(null);
        setVerificationComment('');
        setIsVerified(false);
        setShowRemarksHistory(false);
        setIsLeftPanelCollapsed(false);
        setPendingAction(null);
        dispatch(clearVerifyDetail());
        dispatch(resetApprovalData());
    };

    // ── Init ─────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (effectiveRoleId) loadGrid();
        // Legacy drops 'Return' from the status dropdown
        dispatch(setShowReturnButton('No'));
        return () => {
            dispatch(resetCCClosing());
            dispatch(resetApprovalData());
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, effectiveRoleId]);

    // ── Fetch detail when item selected ──────────────────────────────────────
    useEffect(() => {
        if (selectedItem?.Tranno) {
            dispatch(clearVerifyDetail());
            loadDetail(selectedItem);
            setIsVerified(false);
            setVerificationComment('');
            setShowRemarksHistory(false);
            setResourceRows([]);
            setSummaryLoaded(false);
            setPendingAction(null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem, dispatch]);

    // ── Seed editable fields from the detail ─────────────────────────────────
    useEffect(() => {
        if (!verifyDetail) return;
        const t = String(verifyDetail.SClosingTypeid || '');
        // Legacy: type 7 date picker opens on today; type 5 shows the stored closing date
        setClosingDate(t === '7' ? today() : parseDMY(verifyDetail.SCCClosingDate));
        setStartDate(parseDMY(verifyDetail.SCCStartDate));
        setEndDate(parseDMY(verifyDetail.SCCEndDate));
        setAlertNote(verifyDetail.SAlertnote || '');
    }, [verifyDetail]);

    // ── Fetch status list + remarks when detail loads ────────────────────────
    useEffect(() => {
        if (selectedItem && verifyDetail?.SMOID && effectiveRoleId) {
            dispatch(fetchStatusList({ MOID: verifyDetail.SMOID, ROID: effectiveRoleId, ChkAmt: 0 }));
            dispatch(setSelectedMOID(verifyDetail.SMOID));
            dispatch(fetchRemarks({
                trno: verifyDetail.STranno || selectedItem.Tranno || '',
                moid: verifyDetail.SMOID,
            }));
        }
    }, [selectedItem, verifyDetail?.SMOID, verifyDetail?.STranno, effectiveRoleId, dispatch]);

    // ── Auto-collapse left panel when item selected ───────────────────────────
    useEffect(() => {
        if (selectedItem) setIsLeftPanelCollapsed(true);
    }, [selectedItem]);

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleRefresh = () => {
        loadGrid();
        if (selectedItem?.Tranno) loadDetail(selectedItem);
    };

    const handleItemSelect = (item) => {
        dispatch(clearVerifyDetail());
        setSelectedItem(item);
    };

    const handleAlertNoteChange = (e) => {
        // Legacy: letters, comma and space only, upper-cased, max 50
        setAlertNote(e.target.value.replace(/[^A-Za-z, ]/g, '').toUpperCase().slice(0, 50));
    };

    const submitApproval = async (actionValue) => {
        const d = verifyDetail || {};
        const typeId = String(d.SClosingTypeid || typeIdOf(selectedItem));
        const csv = typeId === '5' ? resourceCsv(resourceRows) : { heads: '', details: '' };
        const payload = {
            STranno:        d.STranno || selectedItem.Tranno || '',
            SId:            String(d.SId || selectedItem.IId || ''),
            SCCClosingDate: (typeId === '5' || typeId === '7') ? toDMY(closingDate) : '',
            SAlertnote:     alertNote.trim(),
            SRemarks:       verificationComment.trim(),
            Appstatus:      actionValue,
            SRoleID:        String(effectiveRoleId),
            SCreatedby:     getCurrentUser(),
            SUserId:        String(userId),
            SCCStartDate:   (typeId === '6' || typeId === '8') ? toDMY(startDate) : '',
            SCCEndDate:     (typeId === '6' || typeId === '8') ? toDMY(endDate) : '',
            SClosingTypeid: typeId,
            ResourceHead:   csv.heads,
            ResDetails:     csv.details,
        };

        try {
            const result = await dispatch(submitApproveCCClose(payload)).unwrap();
            dispatch(clearApproveResult());
            const status = String(typeof result === 'string' ? result : result?.Data ?? '').trim();
            if (status !== 'Submitted') {
                // SP business messages ('Invalid Date', 'Next Level Verification User Does Not Exist', …)
                toast.error(status || 'Error Occurred While Verification', { autoClose: 10000 });
                return;
            }
            toast.success(SUCCESS_MSG[actionValue.toLowerCase()] || 'Submitted Successfully');
            setTimeout(() => {
                loadGrid();
                resetSelection();
            }, 1000);
        } catch (err) {
            const msg = typeof err === 'string' ? err : err?.message || 'Error Occurred While Verification';
            toast.error(msg, { autoClose: 10000 });
        }
    };

    const handleActionClick = (action) => {
        if (!selectedItem || !verifyDetail) { toast.error('No CC closing record selected.'); return; }

        // Legacy sends the status Type as @Action
        const actionValue = String(action.type || action.text || '').trim();
        const typeId = String(verifyDetail.SClosingTypeid || typeIdOf(selectedItem));

        const errors = [];
        if ((typeId === '5' || typeId === '7') && !closingDate) errors.push('Select Closing Date');
        if (typeId === '6' || typeId === '8') {
            if (!startDate) errors.push('Select Start Date');
            if (!endDate)   errors.push('Select End Date');
        }
        if (!alertNote.trim())           errors.push('Enter Alert Note');
        if (!actionValue)                errors.push('Select Status');
        if (!verificationComment.trim()) errors.push('Enter Note');
        if (errors.length) { toast.error(`Please enter all required fields! ${errors.join(', ')}`); return; }

        if (!isVerified) {
            toast.error('Please check the verification checkbox before proceeding.');
            return;
        }

        if (typeId === '5') {
            const missing = resourceRows.some(r => !String(r.detail || '').trim() || String(r.detail).trim() === '0');
            if (missing) { toast.error('Enter Resource Details'); return; }
            if (actionValue.toLowerCase() === 'approve' && parseFloat(verifyDetail.PCCBUDGETTRANSFERTOCAPITAL) > 0) {
                setPendingAction(actionValue);
                return;
            }
        }
        submitApproval(actionValue);
    };

    // ── Filtered list ─────────────────────────────────────────────────────────
    const typeLabelOf = (item) => item.ClosingType || CLOSING_TYPE_LABELS[typeIdOf(item)] || 'CC Closing';

    const filteredItems = verifyList.filter(t => {
        const q = searchQuery.toLowerCase();
        return !q ||
            t.Tranno?.toLowerCase().includes(q)      ||
            t.CCCode?.toLowerCase().includes(q)      ||
            t.ClosingType?.toLowerCase().includes(q) ||
            t.ClosingDate?.toLowerCase().includes(q);
    });

    // ── Left panel: item card ─────────────────────────────────────────────────
    const renderItemCard = (item) => {
        const typeCls = CLOSING_TYPE_COLORS[typeIdOf(item)] || 'bg-indigo-100 text-indigo-700';

        return (
            <div className="p-4">
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-11 h-11 rounded-full border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-800/50 dark:to-purple-800/50 flex items-center justify-center flex-shrink-0">
                        <XCircle className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">
                            {item.CCCode || item.Tranno}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate font-mono">
                            {item.Tranno}
                        </p>
                    </div>
                </div>
                <div className="flex items-center justify-between text-xs">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${typeCls}`}>
                        {typeLabelOf(item)}
                    </span>
                    {item.ClosingDate && (
                        <span className="flex items-center gap-1 text-gray-400">
                            <Calendar className="w-3 h-3" />{item.ClosingDate}
                        </span>
                    )}
                </div>
            </div>
        );
    };

    const renderListItem = (item) => {
        const typeCls = CLOSING_TYPE_COLORS[typeIdOf(item)] || 'bg-indigo-100 text-indigo-700';

        return (
            <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
                <span className="font-semibold text-gray-900 dark:text-white min-w-[120px]">{item.CCCode || item.Tranno}</span>
                <span className="font-mono text-gray-500 dark:text-gray-400 min-w-[110px]">{item.Tranno}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${typeCls}`}>{typeLabelOf(item)}</span>
                {item.ClosingDate && (
                    <span className="ml-auto flex items-center gap-1 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        <Calendar className="w-3 h-3" />{item.ClosingDate}
                    </span>
                )}
            </div>
        );
    };

    const renderCollapsedItem = () => (
        <div className="w-full h-full rounded-lg border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-800/50 dark:to-purple-800/50 flex items-center justify-center">
            <XCircle className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
    );

    // ── Detail content ────────────────────────────────────────────────────────
    const fieldBox = 'bg-white dark:bg-gray-700/50 rounded-xl p-3 border border-indigo-100 dark:border-indigo-800';
    const fieldLabel = 'text-[10px] font-bold text-indigo-500 uppercase tracking-wider mb-1 flex items-center gap-1';

    const renderDetailContent = () => {
        if (!selectedItem) return null;

        const d       = verifyDetail || {};
        const typeId  = String(d.SClosingTypeid || typeIdOf(selectedItem));
        const typeLbl = d.SCCClosingType || typeLabelOf(selectedItem);
        const typeCls = CLOSING_TYPE_COLORS[typeId] || 'bg-indigo-100 text-indigo-700';
        const ccCode  = d.SCCCode || selectedItem.CCCode;
        const calcUrl = buildS3Url(S3_FOLDERS.CC_CLOSING, d.CCFinalCalulationPath);
        const certUrl = buildS3Url(S3_FOLDERS.CC_CLOSING, d.CCComplitionCertificatePath);
        const showActions = typeId !== '5' || summaryLoaded;

        return (
            <div className="space-y-4">

                {detailLoading && (
                    <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-4 border border-indigo-200 dark:border-indigo-700 flex items-center gap-3">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600" />
                        <span className="text-indigo-700 dark:text-indigo-400 text-sm">Loading closing details…</span>
                    </div>
                )}

                {!detailLoading && !verifyDetail && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-5 border border-yellow-200 dark:border-yellow-700">
                        <p className="text-yellow-700 dark:text-yellow-400 text-center text-sm">No details found for this record</p>
                    </div>
                )}

                {verifyDetail && (
                    <div className="rounded-2xl border-2 border-indigo-200 dark:border-indigo-700 overflow-hidden shadow-md">

                        {/* Card header */}
                        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 px-5 py-4 flex items-center justify-between text-white">
                            <div className="flex items-center gap-2">
                                <XCircle className="w-5 h-5" />
                                <span className="font-bold text-sm tracking-wide uppercase">CC Closing Record</span>
                            </div>
                            <span className="px-3 py-1 rounded-full text-xs font-bold bg-white/20 border border-white/30 font-mono">
                                {d.STranno || selectedItem.Tranno}
                            </span>
                        </div>

                        <div className="bg-gradient-to-br from-indigo-50/50 to-purple-50/30 dark:from-gray-800 dark:to-gray-800 p-5 space-y-5">

                            {/* Type 5: CC Closing Summary */}
                            {typeId === '5' && ccCode && (
                                <CCClosingSummaryView ccCode={ccCode} roleName={roleName} onLoaded={setSummaryLoaded} />
                            )}

                            {/* Uploaded closing documents */}
                            {d.CCFinalCalulationPath && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <a href={calcUrl} target="_blank" rel="noopener noreferrer"
                                        className="flex items-center gap-2 text-sm font-bold text-rose-800 dark:text-rose-400 underline">
                                        <FileText className="w-4 h-4" /> Click To View Final Calculation Sheet on CC Status.
                                    </a>
                                    {certUrl && (
                                        <a href={certUrl} target="_blank" rel="noopener noreferrer"
                                            className="flex items-center gap-2 text-sm font-bold text-rose-800 dark:text-rose-400 underline">
                                            <FileText className="w-4 h-4" /> Click To View Uploaded CC Closing Completion Certificate.
                                        </a>
                                    )}
                                </div>
                            )}

                            {/* Type / CC */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                                <div className={fieldBox}>
                                    <p className={fieldLabel}><Hash className="w-3 h-3" /> Closing Type</p>
                                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${typeCls}`}>{typeLbl}</span>
                                </div>
                                <div className={fieldBox}>
                                    <p className={fieldLabel}><Building2 className="w-3 h-3" /> Cost Center</p>
                                    <p className="font-bold text-gray-900 dark:text-white text-sm">{ccCode || '—'}</p>
                                </div>
                                <div className={fieldBox}>
                                    <p className={fieldLabel}><Building2 className="w-3 h-3" /> Cost Center Name</p>
                                    <p className="font-medium text-gray-800 dark:text-gray-200 text-sm">{d.SCCName || '—'}</p>
                                </div>
                            </div>

                            {/* Dates */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                                {typeId === '5' && (
                                    <div className={fieldBox}>
                                        <p className={fieldLabel}><Calendar className="w-3 h-3" /> Closing Date</p>
                                        <p className="font-medium text-gray-800 dark:text-gray-200 text-sm">{d.SCCClosingDate || '—'}</p>
                                    </div>
                                )}
                                {typeId === '7' && (
                                    <div className={fieldBox}>
                                        <p className={fieldLabel}><Calendar className="w-3 h-3" /> Closing Date *</p>
                                        <CustomDatePicker value={closingDate} onChange={setClosingDate} format="DD-MMM-YYYY"
                                            minDate={parseDMY(d.SCCClosingDate)} maxDate={addDays(today(), 30)}
                                            placeholder="Select closing date" />
                                    </div>
                                )}
                                {(typeId === '6' || typeId === '8') && (
                                    <>
                                        <div className={fieldBox}>
                                            <p className={fieldLabel}><Calendar className="w-3 h-3" /> Store Suspend Start Date *</p>
                                            <CustomDatePicker value={startDate} format="DD-MMM-YYYY"
                                                onChange={(v) => { setStartDate(v); if (v && endDate && endDate < v) setEndDate(null); }}
                                                minDate={parseDMY(d.SCCStartDate)} placeholder="Select start date" />
                                        </div>
                                        <div className={fieldBox}>
                                            <p className={fieldLabel}><Calendar className="w-3 h-3" /> Store Suspend End Date *</p>
                                            <CustomDatePicker value={endDate} onChange={setEndDate} format="DD-MMM-YYYY"
                                                minDate={startDate || parseDMY(d.SCCStartDate)} placeholder="Select end date" />
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Alert note (editable, required) */}
                            <div className={fieldBox}>
                                <p className={fieldLabel}><AlertTriangle className="w-3 h-3" /> Alert Note *</p>
                                <input type="text" value={alertNote} onChange={handleAlertNoteChange} maxLength={50}
                                    placeholder="Enter alert note"
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm uppercase bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100" />
                                <p className="text-[10px] text-gray-400 mt-1 text-right">{alertNote.length}/50</p>
                            </div>

                            {/* Type 5: resource consumed (editable details, add heads) */}
                            {typeId === '5' && ccCode && (
                                <ResourceConsumedGrid mode="verify" ccCode={ccCode}
                                    rows={resourceRows} onChange={setResourceRows} />
                            )}
                        </div>
                    </div>
                )}

                <RemarksHistory
                    isOpen={showRemarksHistory}
                    onToggle={() => setShowRemarksHistory(v => !v)}
                    remarks={remarks}
                    loading={remarksLoading}
                    title="Approval History"
                />

                {verifyDetail && (
                    <VerificationInput
                        isVerified={isVerified}
                        onVerifiedChange={setIsVerified}
                        comment={verificationComment}
                        onCommentChange={(e) => setVerificationComment(e.target.value)}
                        config={{
                            checkboxLabel:       '✓ I have reviewed the CC closing record thoroughly',
                            checkboxDescription: 'Including closing type, dates, closing summary and resource details',
                            commentLabel:        'Notes',
                            commentPlaceholder:  'Please enter your verification notes…',
                            commentRequired:     true,
                            commentMaxLength:    500,
                            showCharCount:       true,
                            validationStyle:     'dynamic',
                            checkboxGradient:    'from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20',
                            commentGradient:     'from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20',
                            commentBorder:       'border-indigo-200 dark:border-indigo-700',
                        }}
                    />
                )}

                {verifyDetail && (
                    statusLoading ? (
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
                            <span className="text-gray-500 text-sm">Loading actions…</span>
                        </div>
                    ) : statusError ? (
                        <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-5 border border-red-200 dark:border-red-700">
                            <p className="text-red-600 dark:text-red-400 text-center text-sm">⚠️ Error loading actions: {statusError}</p>
                        </div>
                    ) : !showActions ? (
                        <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-5 border border-indigo-200 dark:border-indigo-700">
                            <p className="text-indigo-700 dark:text-indigo-400 text-center text-sm">Waiting for the CC closing summary to load…</p>
                        </div>
                    ) : !hasActions || !enabledActions?.length ? (
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-5 border border-yellow-200 dark:border-yellow-700">
                            <p className="text-yellow-700 dark:text-yellow-400 text-center text-sm">ℹ️ No actions available for this record</p>
                        </div>
                    ) : (
                        <ActionButtons
                            actions={enabledActions}
                            onActionClick={handleActionClick}
                            loading={approveLoading}
                            isVerified={isVerified}
                            comment={verificationComment}
                            showValidation={true}
                            excludeActions={['send back', 'return']}
                        />
                    )
                )}
            </div>
        );
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="space-y-6">
            <InboxHeader
                title={`${InboxTitle || 'CC Closing Verification'} (${verifyList.length})`}
                subtitle={ModuleDisplayName}
                itemCount={verifyList.length}
                onBackClick={() => onNavigate?.('dashboard', { name: 'Dashboard', type: 'dashboard' })}
                HeaderIcon={XCircle}
                badgeText="CC Closing"
                badgeCount={verifyList.length}
                searchConfig={{
                    enabled:     true,
                    placeholder: 'Search by CC code, transaction no, type…',
                    value:       searchQuery,
                    onChange:    (e) => setSearchQuery(e.target.value),
                }}
                enableViewToggle
            />

            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredItems,
                    selectedItem: selectedItem,
                    onItemSelect: handleItemSelect,
                    renderItem: renderItemCard,
                    renderListItem: renderListItem,
                    renderCollapsedItem: renderCollapsedItem,
                    loading: listLoading,
                    error: null,
                    onRefresh: handleRefresh,
                    config: {
                        title:          'Pending CC Closings',
                        icon:           Clock,
                        emptyMessage:   'No Records Found',
                        itemKey:        'Tranno',
                        enableCollapse: true,
                        enableRefresh:  true,
                        enableHover:    true,
                        maxHeight:      '100%',
                        headerGradient: 'from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20',
                    },
                    renderPopupContent: () => renderDetailContent(),
                    popupConfig: {
                        title: 'CC Closing Verification',
                        icon: XCircle,
                        headerGradient: 'from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20',
                        maxWidth: 'max-w-[80vw]',
                    },
                }}
                right={{
                    selectedItem: selectedItem,
                    loading: detailLoading,
                    renderContent: renderDetailContent,
                    config: {
                        title: 'Closing Details',
                        icon: XCircle,
                        selectedTitle: 'CC Closing Verification',
                        emptyTitle: 'No Record Selected',
                        emptyMessage: 'Select a CC closing record from the list to verify.',
                        headerGradient: 'from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20',
                        maxHeight: 'calc(100vh - 200px)',
                        sticky: true,
                        stickyTop: '1.5rem',
                    },
                }}
            />

            {/* Legacy Viewapprovemodeal: PCC net margin transfer to Capital CC */}
            {pendingAction && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 shadow-xl p-6 space-y-4">
                        <div className="flex justify-center">
                            <div className="w-14 h-14 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                                <CheckCircle2 className="w-7 h-7 text-green-600 dark:text-green-400" />
                            </div>
                        </div>
                        <p className="text-sm text-center text-gray-800 dark:text-gray-200">
                            PCC closing &amp; net margin{' '}
                            <span className="font-bold text-green-600 dark:text-green-400">{verifyDetail?.PCCBUDGETTRANSFERTOCAPITAL}</span>{' '}
                            transferred to Capital CC for the FYYear:{' '}
                            <span className="font-bold">{verifyDetail?.CAPITALFYYEAR}</span>
                            <br />Do you want to continue?
                        </p>
                        <div className="flex justify-center gap-3">
                            <button type="button" onClick={() => setPendingAction(null)}
                                className="px-5 py-2 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold">
                                No
                            </button>
                            <button type="button" disabled={approveLoading}
                                onClick={() => { const a = pendingAction; setPendingAction(null); submitApproval(a); }}
                                className="px-5 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50">
                                Yes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VerifyCCClosing;
